"use client"

import * as React from "react"
import { format, startOfDay } from "date-fns"
import { CalendarIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Portal } from "@radix-ui/react-popover"
import { toast } from "sonner"

interface Props {
  value: any,
  setValue: Function,
  minDate?: Date,
  id?: string
}

const DatePicker: React.FC<Props> = ({ value, setValue, minDate = new Date(), id = 'date' }) => {
  const handleChange = (val: any) => {
    if (startOfDay(val) < startOfDay(minDate)) {
      toast("Cannot select a date earlier than " + format(minDate, "dd MMMM yyyy"))
      return;
    }

    const fakeEvent = {
      currentTarget: {
        id,
        value: val
      },
    }

    setValue(fakeEvent)
  }

  return (
    <Popover>
      <PopoverTrigger asChild className="w-full">
        <Button
          type="button"
          variant={"outline"}
          className={cn(
            "w-full justify-start text-left font-normal",
            !value && "text-muted-foreground"
          )}
        >
          <CalendarIcon />
          {value ? format(value, "yyyy-MM-dd") : <span>Pick a date</span>}
        </Button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent id="dialog" className="w-auto p-0 z-[99] pointer-events-auto" align="start">
          <Calendar
            id="dialog"
            mode="single"
            selected={value}
            onSelect={handleChange}
            required
            initialFocus
            disabled={(date) => startOfDay(date) < startOfDay(minDate)}
          />
        </PopoverContent>
      </Portal>
    </Popover>
  )
}

export default DatePicker
